// v2.1 (D): loads /api/config and, when it has a Clerk key, wraps the app in ClerkProvider (a lazy chunk, so the
// password-only app never downloads Clerk). Also `useGate`: the screen the auth gate shows right now.
import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { useStore } from "../store";
import { clerkEnabled, gate, type Gate } from "./gate";
import { getAuth, loadAuthConfig, setAuth, useAuthState } from "./state";

/** clerk-js didn't load in this long (blocked script, offline): fall back to the password form if there is one. */
const CLERK_LOAD_TIMEOUT_MS = 15_000;

function PassThrough({ children }: { publishableKey: string; children: ReactNode }) {
  return <>{children}</>;
}
const ClerkRoot = lazy(() =>
  import("./clerk")
    .then((m) => ({ default: m.ClerkRoot }))
    .catch(() => {
      setAuth({ clerk: { loaded: false, signedIn: false, failed: true } });
      return { default: PassThrough };
    }),
);

export function AuthRoot({ children }: { children: ReactNode }) {
  const config = useAuthState((s) => s.config);
  useEffect(() => {
    void loadAuthConfig();
  }, []);
  const clerk = clerkEnabled(config);
  useEffect(() => {
    if (!clerk) return;
    const t = window.setTimeout(() => {
      const c = getAuth().clerk;
      if (!c?.loaded) setAuth({ clerk: { loaded: false, signedIn: false, failed: true } });
    }, CLERK_LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [clerk]);
  if (!clerk) return <>{children}</>;
  return (
    <Suspense fallback={children}>
      <ClerkRoot publishableKey={config!.clerkPublishableKey!}>{children}</ClerkRoot>
    </Suspense>
  );
}

export function useGate(path: string): Gate {
  const auth = useStore((s) => s.auth);
  const access = useStore((s) => s.me?.user.access ?? null);
  const geoBlocked = useStore((s) => !!s.me?.user.geoBlocked);
  const termsRequired = useStore((s) => !!s.me?.user.termsRequired);
  const config = useAuthState((s) => s.config);
  const clerk = useAuthState((s) => s.clerk);
  return gate({ config, clerk, auth, access, geoBlocked, termsRequired, path });
}
